import React, { Component } from 'react';
import { observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import httpStatus from 'http-status-codes';
import { find, isEqual } from 'lodash';

import { Well } from 'react-bootstrap';

import { getNameOrDefault } from '@mighty-justice/utils';

import ClientDirectUpload from '../../../clients/ClientDirectUpload';
import { createDisabledContainer, createGuardedContainer } from '../../../containers';
import { ILoanDocument, IReactTableItem } from '../../../interfaces';
import { SessionStoreClass } from '../../../stores';
import { SmartBool, TableRenderingUtils, toast } from '../../../utils';
import { errorResponseStatus, isTransportError } from '../../../utils/transportErrorUtils';
import { ActionButton, DeleteButton, DocumentTable, DocumentUploadModal, Icon, Loader } from '../../common';
import { ConfirmDeletePopover } from '../../common-popovers';

const {
  renderDate,
  renderFilenameLink,
  renderFileType,
} = TableRenderingUtils;

const COL_WIDTH_FILE_ICON = 25
  , COL_WIDTH_BUTTONS = 100
  , COL_WIDTH_DATE = 100
  , COL_MIN_WIDTH_NAME = 150
  , COL_MIN_WIDTH_UPLOADED_BY = 125
  ;

const GUARDED_DELETE_BUTTONS: { [permission: string]: any } = {};

/**
 * createGuardedContainer bakes its permission in when the component type is built, so a delete button
 * taking the permission as a prop isn't possible -- and building one per render would remount it every
 * time the page re-renders. So there is one per lending document type, kept here and looked up by the
 * `deletePermission` the caller passes.
 */
function getGuardedDeleteButton (permissionName: string) {
  if (!GUARDED_DELETE_BUTTONS[permissionName]) {
    GUARDED_DELETE_BUTTONS[permissionName] = createGuardedContainer({
      disabledComponent: createDisabledContainer(DeleteButton),
      enabledComponent: DeleteButton,
      permissionName,
    });
  }

  return GUARDED_DELETE_BUTTONS[permissionName];
}

function renderUploadedBy (item: IReactTableItem) {
  // getNameOrDefault gives the empty field for a document with no uploader, which is any of them
  // filed outside the API -- through the Django admin, say
  return <div className='ellipsis'>{getNameOrDefault(item.value)}</div>;
}

interface IProps {
  // The endpoint's own permissions. Loan account documents and loan documents are separate models,
  // so a user can hold one set without the other.
  addPermission: string;
  deletePermission: string;
  editPermission: string;

  client: ClientDirectUpload;
  // What the documents hang off: {account: id} for an account's documents, {loan: id} for a loan's.
  // Used both to filter the list and as the body of a create.
  parent: { [field: string]: string };
  title: string;
}

interface IInjected extends IProps {
  SessionStore: SessionStoreClass;
}

/**
 * A well of documents filed against a loan account or against one loan, shared by the account's
 * Documents tab and the loan detail modal -- the two differ only in their endpoint, their parent and
 * their permissions.
 *
 * Files never pass through the web server: ClientDirectUpload puts each one straight into storage and
 * then creates the document naming the path it wrote to.
 */
@inject('SessionStore')
@autoBindMethods
@observer
class LoanDocuments extends Component<IProps> {
  @observable private documents: ILoanDocument[] = [];
  @observable private isLoading = true;
  @observable private isUploading = false;
  @observable private refTable: null | object = null;
  @observable private uploadModalShowing = new SmartBool(false);
  // The document being renamed, held outside the observable state as the case documents well does:
  // it is read once when the modal opens, and nothing re-renders on it changing.
  private currentDocument: null | ILoanDocument = null;

  public componentDidMount () {
    this.fetchDocuments();
  }

  public componentWillReceiveProps (nextProps: IProps) {
    // Every caller today gives the component a new instance per parent, but the list is read once on
    // mount while `parent` is read on every create -- so without this the two could disagree, and a
    // caller that reused one instance across parents would upload into a loan it isn't showing.
    if (!isEqual(nextProps.parent, this.props.parent)) {
      this.fetchDocuments(nextProps.parent);
    }
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchDocuments (parent = this.props.parent) {
    const { client } = this.props;

    this.isLoading = true;

    try {
      // The endpoint is unpaginated, so the response is the array itself
      this.documents = await client.list({ params: parent });
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the documents:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  private get canEdit () {
    return this.injected.SessionStore.userHasPermission(this.props.editPermission);
  }

  private get canAdd () {
    return this.injected.SessionStore.userHasPermission(this.props.addPermission);
  }

  private get columns () {
    const { SessionStore } = this.injected
      , GuardedDeleteButton = getGuardedDeleteButton(this.props.deletePermission)
      ;

    return [
      {
        accessor: 'file',
        Cell: renderFileType,
        className: 'col-file',
        Header: null,
        width: COL_WIDTH_FILE_ICON,
      },
      {
        accessor: 'name',
        Cell: renderFilenameLink,
        getViewUrl: SessionStore.getDocumentViewUrl,
        Header: 'Name',
        minWidth: COL_MIN_WIDTH_NAME,
        sortable: true,
      },
      {
        // Where the case documents table shows the document's type. Lending documents have none --
        // the types aren't settled -- and who filed it is the next most useful thing to say.
        accessor: 'uploaded_by',
        Cell: renderUploadedBy,
        className: 'col-style-gray',
        Header: 'Uploaded by',
        minWidth: COL_MIN_WIDTH_UPLOADED_BY,
        sortable: false,
      },
      {
        accessor: 'created_at',
        Cell: renderDate,
        className: 'col-date col-style-gray',
        Header: 'Date',
        sortable: true,
        width: COL_WIDTH_DATE,
      },
      {
        accessor: 'id',
        Cell: (row: IReactTableItem) => {
          const { column: { overlayContainer } } = row;

          return (
            <div>
              {this.canEdit &&
                <ActionButton className='btn-edit-document' data={row.original} onClick={this.onEditDocument}>
                  <Icon type='pencil' />
                </ActionButton>
              }
              <ActionButton
                className='btn-download'
                data={row.original.download_url}
                onClick={SessionStore.downloadDocument}
              >
                <Icon type='download' />
              </ActionButton>
              <ConfirmDeletePopover
                container={overlayContainer}
                data={row.value}
                name='document'
                onDelete={this.onClickDelete}
                placement='left'
                rootClose
              >
                <GuardedDeleteButton />
              </ConfirmDeletePopover>
            </div>
          );
        },
        className: 'col-buttons',
        Header: null,
        overlayContainer: this.refTable,
        width: COL_WIDTH_BUTTONS,
      },
    ];
  }

  private setTableRef (ref: null | object) {
    this.refTable = ref;
  }

  private onEditDocument (data: { id: string }) {
    this.currentDocument = find(toJS(this.documents), { id: data.id }) || null;
    this.uploadModalShowing.setTrue();
  }

  private onCloseUploadModal () {
    this.currentDocument = null;
    this.uploadModalShowing.setFalse();
  }

  /**
   * The modal only ever renames: a new document arrives through the dropzone, whose files go straight
   * to storage rather than through a form field.
   */
  private async onSubmitRename (data: { id: string, name: string }) {
    const { client } = this.props;

    await client.update(data.id, { name: data.name });

    this.currentDocument = null;
    await this.fetchDocuments();
  }

  private async uploadFile (file: File) {
    const { client, parent } = this.props;

    try {
      // `name` is required and there is no form behind a dropped file to ask for one, so it starts
      // as the filename and is renamed from the table afterwards. c.f. CaseDocuments.handleDrop.
      await client.createWithFile(file, { ...parent, name: file.name });
    }
    catch (error) {
      // Only what Transport hasn't already reported: it stays quiet for a 400, and never sees the
      // leg that goes straight to storage. Toasting regardless puts a vaguer message under its.
      if (!isTransportError(error) || errorResponseStatus(error) === httpStatus.BAD_REQUEST) {
        toast.error(`Unable to upload ${file.name}, please try again.`);
      }

      // tslint:disable-next-line no-console
      console.error(`Error uploading ${file.name}:`, error);
    }
  }

  private async onDrop (files: File[]) {
    this.isUploading = true;

    // One at a time: each file is three requests, and a bulk drop would otherwise open all of them
    // against storage at once. Each is caught on its own, being independent of the others.
    for (const file of files) {
      await this.uploadFile(file);
    }

    this.isUploading = false;
    await this.fetchDocuments();
  }

  private async onClickDelete (documentId: string) {
    const { client } = this.props;

    await client.delete(documentId);

    this.documents = this.documents.filter(document => document.id !== documentId);
  }

  /**
   * The heading belongs to DocumentTable, so it arrives with the rows rather than sitting above the
   * loader. Worth it to avoid the alternative: an empty dropzone saying to drop files here, which is
   * what a table rendered against no documents yet looks like.
   */
  private renderTable () {
    const { title } = this.props;

    if (this.isLoading) {
      return <Loader className='bloc-loader' />;
    }

    return (
      <DocumentTable
        columns={this.columns}
        // react-table wants a plain array, not the observable one mobx made of the response
        documents={toJS(this.documents)}
        hasHeader
        isUploading={this.isUploading}
        // Without the add permission a create is rejected, so the dropzone is left off entirely
        onDrop={this.canAdd ? this.onDrop : undefined}
        ref={this.setTableRef}
        title={title}
      />
    );
  }

  public render () {
    return (
      <Well className='loan-documents-list'>
        <form>
          {this.renderTable()}
          {this.uploadModalShowing.isTrue &&
            <DocumentUploadModal
              _document={this.currentDocument}
              onClose={this.onCloseUploadModal}
              onSubmit={this.onSubmitRename}
            />
          }
        </form>
      </Well>
    );
  }
}

export default LoanDocuments;
